import React from 'react';
import { Crown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFeature } from '@/hooks/use-subscription';
import { Button } from '@/components/ui/button';

interface FeatureGateProps {
  featureId: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgrade?: boolean;
  className?: string;
}

/**
 * FeatureGate component that conditionally renders content based on subscription level
 * Shows a crown icon for locked features and prevents access
 */
export function FeatureGate({
  featureId,
  children,
  fallback,
  showUpgrade = true,
  className
}: FeatureGateProps) {
  const { hasAccess } = useFeature(featureId);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <div className={cn("relative", className)}>
      {/* Crown overlay for locked features */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-md">
        <div className="flex flex-row items-center gap-2 text-muted-foreground">
          <Crown className="h-5 w-5 text-yellow-500" />
          {showUpgrade && (
            <span className="text-xs font-medium">
              PRO Feature
            </span>
          )}
        </div>
      </div>

      {/* Disabled content */}
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
    </div>
  );
}

interface FeatureButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  featureId: string;
  showUpgrade?: boolean;
}

/**
 * Button component that shows crown icon when feature is locked
 */
export function FeatureButton({
  featureId,
  children,
  showUpgrade = true,
  onClick,
  className,
  disabled,
  ...props
}: FeatureButtonProps) {
  const { hasAccess } = useFeature(featureId);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasAccess) {
      e.preventDefault();
      // Could show upgrade modal here
      console.log(`Upgrade required for ${featureId}`);
      return;
    }
    onClick?.(e);
  };

  return (
    <Button
      {...props}
      onClick={handleClick}
      disabled={!hasAccess && disabled !== false}
      className={cn(
        className,
        !hasAccess && "relative"
      )}
    >
      {children}
      {!hasAccess && showUpgrade && (
        <Crown className="ml-2 h-4 w-4 text-yellow-500" />
      )}
    </Button>
  );
}

interface FeatureLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  featureId: string;
  showUpgrade?: boolean;
}

/**
 * Link component that shows crown icon when feature is locked
 */
export function FeatureLink({
  featureId,
  children,
  showUpgrade = true,
  onClick,
  href,
  className,
  ...props
}: FeatureLinkProps) {
  const { hasAccess } = useFeature(featureId);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!hasAccess) {
      e.preventDefault();
      console.log(`Upgrade required for ${featureId}`);
      return;
    }
    onClick?.(e);
  };

  return (
    <a
      {...props}
      href={hasAccess ? href : undefined}
      onClick={handleClick}
      className={cn(
        className,
        !hasAccess && "pointer-events-none opacity-60"
      )}
    >
      {children}
      {!hasAccess && showUpgrade && (
        <Crown className="ml-1 h-3 w-3 text-yellow-500 inline" />
      )}
    </a>
  );
}